/**
 *  @organization: Nanocosmos GmbH
 *
 *  Generic statistics helpers, kept apart from the metrics that use them.
 */

export function median(sortedValues) {
   if (sortedValues.length === 0) return null;
   const middle = Math.floor(sortedValues.length / 2);
   return sortedValues.length % 2 === 0 ? (sortedValues[middle - 1] + sortedValues[middle]) / 2 : sortedValues[middle];
}

// Median of every datapoint within [t - window, t], i.e. a trailing window. Driven by timestamps rather than
// by a fixed sample count so a gap in the data shortens the window instead of silently reaching further back.
// Expects points as { x: milliseconds, y: value }, ordered by x, and returns one median per point - the first
// ones therefore over a window the data only partly covers. Use getIntervalWindows where that matters; this
// one is for the callers that need a median per datapoint, index aligned to the input.
export function getRollingMedians(points, windowInSeconds) {
   const medians = [];
   let windowStartIdx = 0;

   points.forEach((point, idx) => {
      while (points[windowStartIdx].x < point.x - windowInSeconds * 1000) {
         windowStartIdx++;
      }
      const windowValues = points
         .slice(windowStartIdx, idx + 1)
         .map((windowPoint) => windowPoint.y)
         .sort((a, b) => a - b);
      medians.push({ x: point.x, y: median(windowValues) });
   });

   return medians;
}

// The datapoints within (t - window, t] for every full interval step t the data covers - the same trailing
// window as above, but evaluated on a fixed grid instead of once per datapoint, so the result holds the
// windows an evaluation running at that interval decides on rather than every intermediate one.
//
// The grid starts at the first step whose window the data reaches back to, so no window is ever only partly
// covered: a caller that wants the windows of a given range has to hand in the datapoints of one window
// length before it as well. Beyond that, a gap still shortens a window - a step whose window holds no
// datapoint at all is left out, which keeps a gap in the data a gap in the result.
//
// The data almost never ends on a step, so the last datapoint gets a window of its own on top of the grid:
// without it the result stops up to a full interval short of the data, which reads as missing data rather
// than as the grid it is. That window is as long as every other one, it just does not end on the grid.
//
// Expects points as { x: milliseconds, y: value }, ordered by x. Steps are aligned to the epoch, so with an
// interval of 60 seconds they land on full UTC minutes. Returns [{ x: step, values: [y, ...] }].
export function getIntervalWindows(points, windowInSeconds, intervalInSeconds) {
   if (points.length === 0) return [];

   const windowInMs = windowInSeconds * 1000;
   const intervalInMs = intervalInSeconds * 1000;
   const lastX = points[points.length - 1].x;
   const firstStep = Math.ceil((points[0].x + windowInMs) / intervalInMs) * intervalInMs;

   const steps = [];
   for (let step = firstStep; step <= lastX; step += intervalInMs) {
      steps.push(step);
   }
   // only where the grid holds a step at all: a range shorter than one window has no complete window to end
   if (steps.length > 0 && steps[steps.length - 1] < lastX) {
      steps.push(lastX);
   }

   const windows = [];
   // both only ever move forward, since the steps do
   let windowStartIdx = 0;
   let windowEndIdx = 0;

   steps.forEach((evaluatedAt) => {
      while (windowStartIdx < points.length && points[windowStartIdx].x <= evaluatedAt - windowInMs) {
         windowStartIdx++;
      }
      while (windowEndIdx < points.length && points[windowEndIdx].x <= evaluatedAt) {
         windowEndIdx++;
      }

      const values = points.slice(windowStartIdx, windowEndIdx).map((windowPoint) => windowPoint.y);

      if (values.length > 0) {
         windows.push({ x: evaluatedAt, values });
      }
   });

   return windows;
}

// The median of every window of getIntervalWindows, i.e. one point per window. Sorts each window's values,
// which is what median() expects, rather than leaving that to every caller.
export function getWindowMedians(windows) {
   return windows.map(({ x, values }) => ({ x, y: median([...values].sort((a, b) => a - b)) }));
}
